import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { Clock, Loader2, RotateCcw, User } from "lucide-react";
import api from "../../api";
import { Card } from "../ui/CCard";

// Shared between the admin Home dashboard and the doctor's (TherapistDashboard)
// - status badge logic (including the stuck-summary retry action) used to be
// duplicated inline in Dashboard.js only; factored out so both "session
// data" views render it identically.
function StatusBadge({ session, onRetried }) {
  const isCompleted = session.summary || session.sessionoutcome || session.summary_generation_status === "completed";
  const isProcessing = session.summary_generation_status === "processing";
  const isFailed = session.summary_generation_status === "failed";

  let isTimeout = false;
  if (isProcessing && session.summary_generation_start_time) {
    const startTime = new Date(session.summary_generation_start_time);
    const diffMins = (new Date() - startTime) / 1000 / 60;
    if (diffMins > 15) isTimeout = true;
  }

  if (isCompleted) {
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
        Completed
      </span>
    );
  }

  if (isProcessing && !isTimeout) {
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700 animate-pulse">
        <Loader2 size={12} className="animate-spin" />
        Generating...
      </span>
    );
  }

  if (isFailed || isTimeout) {
    return (
      <div className="flex items-center gap-2">
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
          {isTimeout ? "Stuck" : "Failed"}
        </span>
        <button
          onClick={async (e) => {
            e.stopPropagation();
            e.preventDefault();
            if (!window.confirm("Restart summary generation?")) return;
            try {
              const toastId = toast.loading("Restarting summary generation...");
              await api.generateConsultationSummary(session.id);
              toast.update(toastId, { render: "Generation restarted!", type: "success", isLoading: false, autoClose: 3000 });
              setTimeout(() => onRetried?.(), 1000);
            } catch (err) {
              console.error(err);
              toast.error("Failed to restart generation");
            }
          }}
          className="p-1.5 hover:bg-gray-200 rounded-full transition-colors text-gray-500 hover:text-gray-700"
          title="Retry Summary Generation"
        >
          <RotateCcw size={14} />
        </button>
      </div>
    );
  }

  return (
    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">
      In Progress
    </span>
  );
}

export default function RecentActivityCard({ sessions = [], onRetried }) {
  return (
    <div>
      <h3 className="text-lg font-bold text-secondary-800 mb-4 px-1">Recent Activity</h3>
      <Card className="overflow-hidden">
        {/* Desktop View */}
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-secondary-50/50 border-b border-secondary-100 text-xs uppercase tracking-wider text-secondary-500 font-semibold">
                <th className="px-6 py-4">Patient</th>
                <th className="px-6 py-4">Date</th>
                <th className="px-6 py-4">Status</th>
                <th className="px-6 py-4 text-right">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-secondary-100">
              {sessions.length > 0 ? (
                sessions.map((session) => (
                  <tr key={session.id} className="hover:bg-secondary-50/30 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center text-primary-700 font-bold text-xs">
                          <User size={14} />
                        </div>
                        <span className="font-medium text-secondary-900">{session.patients?.name || session.patient_name || "Unknown Patient"}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-secondary-500">
                      <div className="flex items-center gap-2">
                        <Clock size={14} />
                        {new Date(session.created_at).toLocaleDateString()}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <StatusBadge session={session} onRetried={onRetried} />
                    </td>
                    <td className="px-6 py-4 text-right">
                      <Link to={`/consultation/${session.id}`} className="text-primary-600 hover:text-primary-700 text-sm font-medium hover:underline">
                        View Details
                      </Link>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={4} className="px-6 py-12 text-center text-secondary-400 italic">
                    No recent activity found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Mobile View */}
        <div className="md:hidden divide-y divide-secondary-100">
          {sessions.length > 0 ? (
            sessions.map((session) => (
              <div key={session.id} className="p-4 hover:bg-secondary-50/30 transition-colors">
                <div className="flex justify-between items-start mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center text-primary-700 font-bold">
                      <User size={16} />
                    </div>
                    <div>
                      <div className="font-bold text-secondary-900 text-base">{session.patients?.name || session.patient_name || "Unknown Patient"}</div>
                      <div className="flex items-center gap-1.5 text-xs text-secondary-500 mt-1">
                        <Clock size={12} />
                        {new Date(session.created_at).toLocaleDateString()}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="flex items-center justify-between mt-4">
                  <div>
                    <StatusBadge session={session} onRetried={onRetried} />
                  </div>
                  <Link to={`/consultation/${session.id}`} className="text-primary-600 hover:text-primary-700 text-sm font-semibold flex items-center gap-1 bg-primary-50 px-3 py-1.5 rounded-lg">
                    View Details
                  </Link>
                </div>
              </div>
            ))
          ) : (
            <div className="p-8 text-center text-secondary-400 italic">
              No recent activity found.
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}
